import type { RoundProgress } from './leadRounds';

/**
 * How far through a round a lead is, drawn.
 *
 * ── Why a bar is allowed on a dense table at all ───────────────────────────
 *
 * docs/design-rule.md: a value that appears on 90% of rows must never be the
 * most decorated thing on the row. This passes because the Rounds cell is
 * BLANK for every lead nobody has started a round on, which on the measured
 * data is most of the table. The bar draws only where somebody is mid-round,
 * which is exactly what visual weight is for.
 *
 * ── It ripens; it does not judge ───────────────────────────────────────────
 *
 * Kyu, 29 Aug: "maybe start from light green to dark green at the very end of
 * the progress bar. Red can imply lack of action when it's not, we're just not
 * done yet."
 *
 * So there is no red on this bar in any state. Being at 3 of 7 is an
 * unfinished job, not a failure, and painting it like a fault would spend the
 * page's loudest colour on the ordinary case.
 *
 * The gradient is painted across the WHOLE track and revealed by the fill, so
 * the shade is a property of how far along you are rather than of the fill's
 * own length: 4 of 7 shows the same colour at the same place whether the
 * target is five or seven. Painted on the fill instead, a 2/7 bar and a 7/7
 * bar would each run the full ramp and the colour would say nothing.
 *
 * ── The pace mark ──────────────────────────────────────────────────────────
 *
 * One hairline showing where the window is today. It carries what the old red
 * "Behind" badge used to: falling short reads as a visible gap between the
 * fill and the mark, rather than as an accusation. It is hidden once the
 * window is done, where it would sit on the end and mean nothing.
 */
export function RoundBar({ progress }: { progress: RoundProgress | null }) {
  if (progress === null || progress.touchTarget <= 0) {
    return null;
  }
  const done = clampPercent((progress.touchesDone / progress.touchTarget) * 100);
  // Where the window has got to, in the same 0-100 space as the fill.
  //
  // windowDays, NOT touchTarget. Written first with touchTarget, which is only
  // the same number on a 7-over-7 round: a 3-over-2-weeks round would have put
  // the pace mark a third of the way along on its first day.
  const windowTotal = progress.windowDays;
  const paceShown = windowTotal > 0 && progress.daysLeft > 0 && progress.daysLeft < windowTotal;
  const pace = clampPercent(((windowTotal - progress.daysLeft) / windowTotal) * 100);

  return (
    <div
      className="round-track"
      // Presentation only. The cell already says "Round 2 · 3/7" in text
      // immediately above, so a screen reader that also announced this would
      // read the same fact twice.
      aria-hidden="true"
      style={paceShown ? { ['--pace' as string]: `${String(pace)}%` } : undefined}
    >
      <div className="round-rest" style={{ ['--done' as string]: `${String(done)}%` }} />
    </div>
  );
}

function clampPercent(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.min(100, Math.round(value)));
}
